/**
 * MemberDocumentsDialog — v1.0.7 Batch B: per-member document uploads from
 * the Household Profile. Files attach to the MEMBER's own screening record
 * (files are keyed by screening_id), never to the primary by default.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Paperclip } from "lucide-react";
import { toast } from "sonner";
import { SingleFileField, MultiFileField, uploadFileToScreening } from "@/components/FileUploadFields";

export default function MemberDocumentsDialog({ screeningId, memberName, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [rentalApp, setRentalApp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");

  const reset = () => { setRentalApp(null); setDocs([]); setProgress(""); };

  const upload = async () => {
    setBusy(true);
    let okCount = 0;
    try {
      if (rentalApp) {
        setProgress("Uploading rental application…");
        const r = await uploadFileToScreening(screeningId, rentalApp, "rental_application");
        if (r.ok) okCount += 1; else toast.error(`${rentalApp.name} — ${r.error}`);
      }
      for (let i = 0; i < docs.length; i++) {
        setProgress(`Uploading documents (${i + 1}/${docs.length})…`);
        const r = await uploadFileToScreening(screeningId, docs[i], "supporting_document");
        if (r.ok) okCount += 1; else toast.error(`${docs[i].name} — ${r.error}`);
      }
      if (okCount > 0) {
        toast.success(`${okCount} file${okCount === 1 ? "" : "s"} attached to ${memberName}'s screening`);
        setOpen(false);
        reset();
        onUploaded?.();
      }
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full rounded-md h-9 border-slate-300 text-slate-700 hover:border-slate-900"
                data-testid={`member-docs-btn-${screeningId}`}>
          <Paperclip className="h-3.5 w-3.5 mr-1.5" /> Upload documents
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`member-docs-dialog-${screeningId}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Documents for {memberName}</DialogTitle>
          <DialogDescription>
            Files attach directly to this member&apos;s screening record and appear in their
            document list for NTN verification.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <SingleFileField
            file={rentalApp}
            onChange={setRentalApp}
            testidPrefix={`member-docs-rental-${screeningId}`}
            title="Rental application PDF (optional)"
            compact
          />
          <MultiFileField
            files={docs}
            onChange={setDocs}
            testidPrefix={`member-docs-supporting-${screeningId}`}
            title="Supporting documents"
            hint="Paystubs, bank statements, IDs, W-2s — anything relevant to this member's verification."
            compact
          />
        </div>
        <DialogFooter>
          <Button onClick={upload} disabled={busy || (!rentalApp && docs.length === 0)}
                  className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm"
                  data-testid={`member-docs-upload-${screeningId}`}>
            {busy ? (progress || "Uploading…") : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
